import type { Difficulty, Locale } from "@divespots/contracts";
import { DIFFICULTIES, slugify } from "@divespots/contracts";
import { useMemo } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ClosedPeriodsEditor, RatingsEditor } from "./editors.tsx";
import { TEXT_KEYS, parseLatLon } from "./forms.ts";
import type { FieldErrors, SpotForm } from "./forms.ts";
import { LocationPicker } from "./LocationPicker.tsx";
import { Field, LocaleField, LocaleTabs, Section, errorAt } from "./ui.tsx";

export const DEFAULT_CENTER = { lat: 46.8, lon: 8.2 };

export interface SpotFieldsProps {
  /** Staff also set the slug; divers never do (the server derives it) and never set a status. */
  mode: "staff" | "diver";
  form: SpotForm;
  errors: FieldErrors;
  set: <K extends keyof SpotForm>(k: K, v: SpotForm[K]) => void;
  setForm: Dispatch<SetStateAction<SpotForm>>;
  setErrors: Dispatch<SetStateAction<FieldErrors>>;
  /** Called after any edit (the editor clears its "saved" note). */
  onEdited?: () => void;
  textLocale: Locale;
  setTextLocale: (l: Locale) => void;
  /** Rendered at the end of the basics section (the staff editor shows the lake there). */
  basicsExtra?: ReactNode;
}

/** The fields shared by the staff spot editor and the diver's spot suggestion: everything but status and slug logic. */
export function SpotFields({
  mode,
  form,
  errors,
  set,
  setForm,
  setErrors,
  onEdited,
  textLocale,
  setTextLocale,
  basicsExtra,
}: SpotFieldsProps) {
  const { t } = useTranslation();
  const derived = useMemo(() => slugify(form.name) || "spot", [form.name]);
  const center = parseLatLon(form.lat, form.lon) ?? DEFAULT_CENTER;
  const textFields = TEXT_KEYS.map((k) => form.texts[k]);
  return (
    <>
      <Section title={t("admin.section.basics")}>
        <Field label={`${t("admin.field.name")} *`} error={errorAt(errors, "name")}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="adm-input"
              value={form.name}
              maxLength={120}
              autoComplete="off"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => set("name", e.target.value)}
            />
          )}
        </Field>
        {mode === "staff" ? (
          <Field
            label={t("admin.field.slug")}
            error={errorAt(errors, "slug")}
            hint={
              form.slug.trim() === ""
                ? t("admin.field.slugDerived", { slug: derived })
                : t("admin.field.slugHint")
            }
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input ds-text-coordinate"
                value={form.slug}
                maxLength={80}
                placeholder={derived}
                autoComplete="off"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set("slug", e.target.value)}
              />
            )}
          </Field>
        ) : null}
        <div className="adm-period__dates">
          <Field label={t("admin.field.waterBody")} error={errorAt(errors, "waterBody")}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input"
                value={form.waterBody}
                maxLength={120}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set("waterBody", e.target.value)}
              />
            )}
          </Field>
          <Field label={t("admin.field.locality")} error={errorAt(errors, "locality")}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input"
                value={form.locality}
                maxLength={120}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set("locality", e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="adm-period__dates">
          <Field label={t("admin.field.difficulty")} error={errorAt(errors, "difficulty")}>
            {({ id, describedBy, invalid }) => (
              <select
                id={id}
                className="adm-input"
                value={form.difficulty}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set("difficulty", e.target.value as Difficulty | "")}
              >
                <option value="">{t("admin.field.none")}</option>
                {DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>
                    {t(`difficulty.${d}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t("admin.field.maxDepth")} error={errorAt(errors, "maxDepthM")}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input ds-text-coordinate"
                inputMode="decimal"
                value={form.maxDepthM}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set("maxDepthM", e.target.value)}
              />
            )}
          </Field>
        </div>
        {basicsExtra}
      </Section>

      <Section title={t("admin.section.location")}>
        <LocationPicker
          label={t("admin.field.entry")}
          lat={form.lat}
          lon={form.lon}
          fallbackCenter={DEFAULT_CENTER}
          errors={{ lat: errors["location.lat"] ?? errors.location, lon: errors["location.lon"] }}
          onChange={(lat, lon) => {
            setForm((f) => ({ ...f, lat, lon }));
            setErrors((e) => ({ ...e, location: "", "location.lat": "", "location.lon": "" }));
            onEdited?.();
          }}
        />
        <label className="adm-check">
          <input
            type="checkbox"
            checked={form.hasParking}
            onChange={(e) => set("hasParking", e.target.checked)}
          />
          <span>{t("admin.field.hasParking")}</span>
        </label>
        {form.hasParking ? (
          <LocationPicker
            label={t("admin.field.parking")}
            lat={form.parkLat}
            lon={form.parkLon}
            fallbackCenter={center}
            contextPoint={parseLatLon(form.lat, form.lon) ?? undefined}
            showDepth={false}
            errors={{
              lat: errors["parkingLocation.lat"] ?? errors.parkingLocation,
              lon: errors["parkingLocation.lon"],
            }}
            onChange={(parkLat, parkLon) => {
              setForm((f) => ({ ...f, parkLat, parkLon }));
              setErrors((e) => ({
                ...e,
                parkingLocation: "",
                "parkingLocation.lat": "",
                "parkingLocation.lon": "",
              }));
              onEdited?.();
            }}
          />
        ) : null}
      </Section>

      <Section title={t("admin.section.texts")}>
        <LocaleTabs
          label={t("admin.i18n.language")}
          value={textLocale}
          onChange={setTextLocale}
          fields={textFields}
        />
        {TEXT_KEYS.map((k) => (
          <LocaleField
            key={k}
            label={t(`sections.${k}`)}
            multiline
            value={form.texts[k]}
            locale={textLocale}
            error={errorAt(errors, k, `${k}.${textLocale}`)}
            onChange={(v) => {
              setForm((f) => ({ ...f, texts: { ...f.texts, [k]: v } }));
              onEdited?.();
            }}
          />
        ))}
      </Section>

      <Section title={t("admin.section.closed")}>
        <p className="ds-text-footnote text-label-secondary m-0">{t("admin.closed.help")}</p>
        <ClosedPeriodsEditor
          value={form.closedPeriods}
          locale={textLocale}
          errors={errors}
          onChange={(v) => set("closedPeriods", v)}
        />
      </Section>

      {mode === "staff" ? (
        <Section title={t("admin.section.permanent")}>
          <p className="ds-text-footnote text-label-secondary m-0">{t("admin.permanent.help")}</p>
          <label className="adm-check">
            <input
              type="checkbox"
              checked={form.permanentClosed}
              onChange={(e) => set("permanentClosed", e.target.checked)}
            />
            {t("admin.permanent.toggle")}
          </label>
          {form.permanentClosed ? (
            <>
              <Field
                label={t("admin.permanent.since")}
                error={errorAt(errors, "permanentClosure.since")}
              >
                {({ id, describedBy, invalid }) => (
                  <input
                    id={id}
                    className="adm-input ds-text-coordinate"
                    inputMode="numeric"
                    placeholder="2024-06-01"
                    value={form.closedSince}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    onChange={(e) => set("closedSince", e.target.value)}
                  />
                )}
              </Field>
              <LocaleTabs
                label={t("admin.i18n.language")}
                value={textLocale}
                onChange={setTextLocale}
                fields={[form.closedReason]}
              />
              <LocaleField
                label={t("admin.permanent.reason")}
                multiline
                value={form.closedReason}
                locale={textLocale}
                error={errorAt(
                  errors,
                  "permanentClosure.reason",
                  `permanentClosure.reason.${textLocale}`,
                )}
                onChange={(v) => set("closedReason", v)}
              />
            </>
          ) : null}
        </Section>
      ) : null}

      <Section title={t("admin.section.ratings")}>
        <RatingsEditor value={form.ratings} onChange={(v) => set("ratings", v)} />
      </Section>
    </>
  );
}
